import type { ReactNode } from "react";
import styled from "styled-components";

import { CameraIcon } from "../../icons/glyphs";
import { Text, defaultTextVariants } from "../Text";
import { TruncatedText } from "../TruncatedText";
import { yScrollbarStyles } from "../foundations/scrollbar";
import {
  ANNOTATION_CAPTURED_FRAME_CONTENT_MIN_HEIGHT,
  ANNOTATION_CAPTURED_FRAME_PREVIEW_MIN_HEIGHT,
  ANNOTATION_CAPTURED_FRAME_SECTION_MIN_HEIGHT,
  ANNOTATION_INSPECTOR_MIN_HEIGHT,
  ANNOTATION_PANEL_BODY_GAP,
  ANNOTATION_PANEL_GAP,
  ANNOTATION_PANEL_HEADER_MIN_HEIGHT,
  ANNOTATION_PANEL_LEAD_SECTION_GAP,
  ANNOTATION_PANEL_LEAD_SECTION_MAX_SHARE,
  ANNOTATION_PANEL_LEAD_SECTION_MIN_HEIGHT,
  ANNOTATION_PANEL_PADDING,
  CAPTURED_FRAME_FILTER_SUMMARY_EXTRA_HEIGHT,
  PANEL_SECTION_GAP,
  PANEL_SECTION_PADDING_TOP,
  STUDIO_BREAKPOINT,
} from "./MediaAnnotationStudioConstraint";

export const AnnotationPanelRoot = styled.aside<{ $visible: boolean }>`
  display: ${({ $visible }) => ($visible ? "flex" : "none")};
  min-width: 0;
  min-height: 0;
  flex-direction: column;
  gap: ${ANNOTATION_PANEL_GAP};
  padding: ${ANNOTATION_PANEL_PADDING};
  overflow: hidden;
  border-left: 1px solid ${({ theme }) => theme.color.border.divider};
  background: ${({ theme }) => theme.color.bg.surfaceSunken};

  @container media-annotation-studio (max-width: ${STUDIO_BREAKPOINT.stackedPanel}) {
    border-top: 1px solid ${({ theme }) => theme.color.border.divider};
    border-left: 0;
  }
`;

export const AnnotationPanelHeader = styled.div`
  display: flex;
  min-width: 0;
  min-height: ${ANNOTATION_PANEL_HEADER_MIN_HEIGHT};
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;

  &[hidden] {
    display: none;
  }
`;

export const AnnotationPanelHeaderTitle = styled.div`
  display: flex;
  min-width: 0;
  flex: 1;
  align-items: center;
  gap: 0.45rem;
`;

export const AnnotationPanelHeaderActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;

  &[hidden] {
    display: none;
  }
`;

export const AnnotationCameraScope = styled.span`
  display: inline-flex;
  min-width: 0;
  max-width: min(12rem, 55%);
  align-items: center;
  gap: 0.25rem;
  color: ${({ theme }) => theme.color.text.subtle};
  font-size: ${({ theme }) => theme.typography.size.caption};
  font-weight: ${({ theme }) => theme.typography.weight.regular};

  & > svg {
    flex: 0 0 auto;
  }
`;

export const AnnotationCameraName = styled(TruncatedText)`
  min-width: 0;
`;

export const PanelSection = styled.section`
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: ${PANEL_SECTION_GAP};
  padding-top: ${PANEL_SECTION_PADDING_TOP};
  border-top: 1px solid ${({ theme }) => theme.color.border.divider};

  &[hidden] {
    display: none;
  }
`;

export const AnnotationInspectorRoot = styled(PanelSection)`
  /* 도구 오버레이의 기준면. 오버레이는 이 섹션 밖으로 나가지 않는다. */
  position: relative;
  /* 현재 페이지를 표시하고 남은 높이를 슬롯 도구가 우선 사용한다. */
  min-height: ${ANNOTATION_INSPECTOR_MIN_HEIGHT};
  flex: 1 1 0;
  overflow: hidden;
  padding-top: 0;
  border-top: 0;

  &:has(> [data-preview-layout="inline"]) {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: minmax(0, max-content) minmax(
        ${ANNOTATION_CAPTURED_FRAME_PREVIEW_MIN_HEIGHT},
        1fr
      );
  }
`;

/** 도구는 자연 높이를 쓰고 미리보기가 남는 높이를 채운다. 최소 높이는 목록보다 먼저 확보한다. */
export const InlineFramePreview = styled.div`
  display: flex;
  min-width: 0;
  min-height: ${ANNOTATION_CAPTURED_FRAME_PREVIEW_MIN_HEIGHT};

  /* 제품의 편집 잠금은 슬롯 도구만 덮는다. 읽기 전용 미리보기는 계속 사용할 수 있다. */
  & + [data-annotation-tools-overlay] {
    grid-column: 1 / 2;
    grid-row: 1 / 2;
  }
`;

export const AnnotationInspectorBody = styled.div`
  display: flex;
  min-width: 0;
  min-height: 0;
  flex: 1;
  flex-direction: column;
  gap: 0.35rem;
  padding-right: 0.1rem;
  ${yScrollbarStyles}
  overflow-y: auto;
`;

/**
 * 도구 섹션을 덮는 제품 소유 층의 자리. 앵커에 붙는 팝업이 아니라 이 섹션 하나를 덮는 정적
 * 층이라 `AnchoredPopup` 대신 섹션 안 absolute 로 둔다 — 뷰포트 밖으로 나갈 일이 없어 flip·clamp 가
 * 필요 없고, 같은 섹션의 마지막 자식이라 z 없이 DOM 순서가 순서를 정한다.
 *
 * 캡처 프레임 섹션은 덮지 않는다 — 도구가 잠겨도 저장된 프레임을 훑어보는 것은 정당한 읽기 행위다.
 * c6 는 scrim 만 그리고 그 위 읽기 면은 제품이 갖는다.
 */
export const AnnotationToolsOverlay = styled.div`
  position: absolute;
  display: flex;
  min-width: 0;
  flex-direction: column;
  align-items: center;
  /* safe center: 내용이 섹션보다 크면 가운데 정렬이 위쪽을 잘라내므로 시작 정렬로 물러난다. */
  justify-content: safe center;
  padding: 0.5rem;
  background: ${({ theme }) => theme.color.scrim.overlay};
  inset: 0;
  ${yScrollbarStyles}
  overflow-y: auto;
`;

export const AnnotationInspectorTools = styled.fieldset`
  display: contents;
  min-width: 0;
  padding: 0;
  border: 0;

  &:disabled {
    color: ${({ theme }) => theme.color.text.disabled};
  }
`;

export const CapturedFramePanelContent = styled.div<{ $filterActive?: boolean }>`
  display: flex;
  min-width: 0;
  /* 범위 필터 + 목록 한 행 + 페이지네이션을 덮는다 — 목록만 세면 페이지네이션이 밀려난다. */
  min-height: calc(
    ${ANNOTATION_CAPTURED_FRAME_CONTENT_MIN_HEIGHT} -
      ${({ $filterActive = true }) =>
        $filterActive ? "0rem" : CAPTURED_FRAME_FILTER_SUMMARY_EXTRA_HEIGHT}
  );
  flex: 1 1 auto;
  flex-direction: column;
  gap: ${PANEL_SECTION_GAP};
`;

export const CapturedFrameSectionRoot = styled(PanelSection)<{ $filterActive?: boolean }>`
  box-sizing: border-box;
  height: auto;
  /* 목록 한 행 + 페이지네이션까지 확보한다. 초과분은 패널 본문이 스크롤한다. */
  min-height: calc(
    ${ANNOTATION_CAPTURED_FRAME_SECTION_MIN_HEIGHT} -
      ${({ $filterActive = true }) =>
        $filterActive ? "0rem" : CAPTURED_FRAME_FILTER_SUMMARY_EXTRA_HEIGHT}
  );
  /* 목록은 현재 페이지에 필요한 높이만 사용한다. */
  flex: 0 1 auto;
  justify-content: flex-start;
  overflow: hidden;
  margin-top: 0;
`;

/**
 * 제품 노드가 서는 패널 선두 자리. 첫 자식이라 위쪽 divider 대신 아래쪽으로 경계를 둔다.
 *
 * 제품이 무엇을 심을지 c6 는 모르므로 자리 자체가 상한을 갖고, 넘치면 자리가 세로로 스크롤한다.
 * 그리고 세로가 모자라면 아래 섹션보다 **먼저 양보한다**(`flex: 0 1 auto` + 바닥) — 자기 스크롤러를
 * 이미 갖고 있어 줄어도 내용에 닿을 수 있다는, 패널 전체와 같은 양보 기준을 따른다. 자리가 양보하지
 * 않으면 키가 큰 제품 노드 하나가 도구·캡처 프레임을 자기 바닥 아래로 밀어 다시 잘림을 만든다.
 */
export const AnnotationPanelLeadSection = styled.div`
  /* 상한·바닥이 자리의 바깥 높이를 뜻하게 한다 — content box 기준이면 padding·border 만큼 넘친다. */
  box-sizing: border-box;
  display: flex;
  min-width: 0;
  min-height: ${ANNOTATION_PANEL_LEAD_SECTION_MIN_HEIGHT};
  max-height: ${ANNOTATION_PANEL_LEAD_SECTION_MAX_SHARE};
  flex: 0 1 auto;
  flex-direction: column;
  gap: ${PANEL_SECTION_GAP};
  padding-bottom: ${ANNOTATION_PANEL_LEAD_SECTION_GAP};
  border-bottom: 1px solid ${({ theme }) => theme.color.border.divider};
  ${yScrollbarStyles}
  overflow-y: auto;
`;

export const AnnotationPanelBody = styled.div`
  display: flex;
  min-width: 0;
  min-height: 0;
  flex: 1;
  flex-direction: column;
  gap: ${ANNOTATION_PANEL_BODY_GAP};
  ${yScrollbarStyles}
  overflow-y: auto;
`;

export const SectionHeading = styled(Text)`
  color: ${({ theme }) => theme.color.text.subtle};
  font-weight: ${({ theme }) => theme.typography.weight.medium};
`;

const ControlsPlaceholderRoot = styled(PanelSection)`
  min-height: 5rem;
  flex: 1 1 auto;
  align-items: center;
  justify-content: center;
  padding: 0.8rem 1rem;
  border-radius: ${({ theme }) => theme.shape.radius.control};
  background: ${({ theme }) => theme.color.bg.surface};
  text-align: center;
`;

const ControlsPlaceholderContent = styled.div`
  display: flex;
  min-width: 0;
  max-width: 100%;
  flex: 0 0 auto;
  flex-direction: column;
  align-items: center;
  gap: ${PANEL_SECTION_GAP};
`;

const ControlsPlaceholderText = styled(Text)`
  max-width: 18rem;
  color: ${({ theme }) => theme.color.text.subtle};
`;

const ControlsPlaceholderAction = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 0.6rem;
`;

export function AnnotationCameraLabel({ label, ariaLabel }: { label: string; ariaLabel: string }) {
  return (
    <AnnotationCameraScope aria-label={ariaLabel}>
      <CameraIcon aria-hidden="true" />
      <AnnotationCameraName fullText={label}>{label}</AnnotationCameraName>
    </AnnotationCameraScope>
  );
}

export function AnnotationControlsPlaceholder({
  children,
  action,
}: {
  children: string;
  /** 안내가 가리키는 다음 행동을 문구 바로 옆에서 실행하는 슬롯(예: 프레임 캡처). */
  action?: ReactNode;
}) {
  return (
    <ControlsPlaceholderRoot data-annotation-controls-placeholder="">
      <ControlsPlaceholderContent data-annotation-placeholder-content="">
        <ControlsPlaceholderText variant={defaultTextVariants["body-normal"]}>
          {children}
        </ControlsPlaceholderText>
        {action ? <ControlsPlaceholderAction>{action}</ControlsPlaceholderAction> : null}
      </ControlsPlaceholderContent>
    </ControlsPlaceholderRoot>
  );
}
